// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    addToChainAfter,
    addToChainBefore,
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
    HasOnSuccess,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import {
    GetProductCollectionApiV2Parameter,
    getProductCollectionV2,
    GetRelatedProductCollectionApiV2Parameter,
    getRelatedProductsCollectionV2,
} from "@insite/client-framework/Services/ProductServiceV2";

import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

import { getFeaturedProducts, GetFeaturedProductsApiParameter } from "@gcg/App/Services/FeaturedProductsService";
import {
    chain,
    PopulateApiParameter,
    RequestDataFromApi,
} from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";

type Parameter = (GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter) &
    HasOnSuccess<ProductModel[]>;
type Props = {
    apiParameter: GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter;
    apiResult: ProductCollectionModel;
};

type HandlerType = Handler<Parameter, Props>;

/**
 *
 * @param props
 * @override clean the props in case we have includeProductsInSubCategories and there is no categoryId which will cause an API error since its
 * required to have a specific categoryId for this condition
 */
export const XngModifyParametersAfterPopulate: any = (props: any) => {
    if (
        !props.parameter?.hasOwnProperty("categoryId") &&
        props.parameter?.hasOwnProperty("includeProductsInSubCategories")
    ) {
        props.parameter.includeProductsInSubCategories = false;
    }
};

/**
 * @param props
 * @override make custom call for featured products carousel
 */
export const XngRequestDataFromApi: HandlerType = async props => {
    if ("relationship" in props.apiParameter) {
        if (props.apiParameter.relationship === "featuredProducts") {
            // send a request to a custom api to receive the fetured products for the passed category/ brand
            const customProps: any = props.apiParameter;
            const id =
                customProps.brand && customProps.category
                    ? customProps.brand.id.split(":")[0]
                    : !customProps.brand
                    ? customProps.category.id.split(":")[0]
                    : customProps.brand.id.split(":")[0];
            const type =
                customProps.brand && customProps.category ? "brand" : !customProps.brand ? "category" : "brand";
            const param: GetFeaturedProductsApiParameter = {
                id,
                type,
            };
            props.apiResult = await getFeaturedProducts(param);
        } else {
            props.apiResult = await getRelatedProductsCollectionV2(props.apiParameter);
        }
    } else {
        props.apiResult = await getProductCollectionV2(props.apiParameter);
    }
};

addToChainBefore(chain, PopulateApiParameter, XngModifyParametersAfterPopulate);
replaceInChain(chain, RequestDataFromApi, XngRequestDataFromApi);
